import { useState, useEffect, useRef, useCallback } from "react";
import type { StateMap } from "../types";
import type { HiddenMap } from "../lib/hiddenFixes";
import { AuditSection } from "./AuditSection";
import { SchemaHistory } from "./SchemaHistory";
import { showAlert, showConfirm } from "../lib/dialog";
import { wireCardTrash } from "../lib/cardTrash";
import { loadAnthropicKey } from "./SettingsModal";
import { scheduleFlush } from "../lib/siteData";

interface Props {
  state: StateMap;
  siteBrand?: string;
  auditDate?: string;
  onApprove: (id: string, message?: string, isCreate?: boolean) => void;
  onIgnore: (id: string, checked: boolean) => void;
  onDone: (id: string, checked: boolean) => void;
  onReset: (id: string) => void;
  hidden: HiddenMap;
  onToggleHide: (id: string) => void;
  connected: boolean;
  fetchPageTags: (pathSlug: string) => Promise<string[] | null>;
  onOpenSchemaSetup?: () => void;
  onViewSchema?: (json: string, url: string) => void;
  onViewIssues?: (issues: string[], url: string) => void;
  manualSchema?: Set<string>;
  onManualUpdate?: (id: string) => void;
  onPredictManual?: (id: string) => void;
  getClient?: () => import("../lib/wpClient").WPClient | null;
}

function decodeEntities(s: string): string {
  return s.replace(/&amp;/g, "&").replace(/&#39;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">");
}
// Normalize for dup-matching: case-insensitive, ignore a trailing slash.
// Dedup key for a URL — deliberately loose so the same page entered a different way maps to one
// card: decode entities, lowercase, then drop protocol (http/https), a leading www., any
// query string / fragment, and trailing slashes. (Matches the Sitemap tab's matcher.)
function normalizeUrl(u: string): string {
  return decodeEntities(u).trim().toLowerCase()
    .replace(/^https?:\/\//, "") // http/https treated the same
    .replace(/^www\./, "")        // with/without www treated the same
    .replace(/[#?].*$/, "")       // ignore query string / fragment
    .replace(/\/+$/, "");         // ignore trailing slash(es)
}
// URLs that already have a card in the current HTML (each card has one .visit-link → page URL).
function extractCardedUrls(html: string): Set<string> {
  const set = new Set<string>();
  const re = /<a href="([^"]+)"[^>]*class="visit-link"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) set.add(normalizeUrl(m[1]));
  return set;
}
// Next free fix-id index so appended cards don't collide with existing sc-bp-N ids.
function nextCardIndex(html: string): number {
  let max = -1;
  const re = /id="oc-sc-bp-(\d+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) { const n = parseInt(m[1], 10); if (n > max) max = n; }
  return max + 1;
}

// Module-level so an in-flight crawl (and its result) survives this component unmounting when the
// user switches tabs. The fetch keeps running, writes its cards straight to localStorage, and a
// remount re-attaches to the same promise to reflect progress + the final result.
let bpsInFlight: Promise<void> | null = null;
let bpsStatus = "";

export function BulkPageSchema(props: Props) {
  // Persisted so the pasted URLs and generated schema cards survive tab switches.
  const [urls, setUrls] = useState(() => { try { return localStorage.getItem("lsg_bps_urls") ?? ""; } catch { return ""; } });
  const [cardsHtml, setCardsHtml] = useState(() => { try { return localStorage.getItem("lsg_bps_cards") ?? ""; } catch { return ""; } });
  const [building, setBuilding] = useState(false);
  const [interrupted, setInterrupted] = useState<string[]>([]); // URLs from a crawl cut off by a reload/close
  const [status, setStatus] = useState("");
  const cardsRef = useRef<HTMLDivElement | null>(null);

  // Per-site (AUDIT_KEYS) — scheduleFlush so generate/delete/edit syncs to the server; without it a
  // refresh re-hydrates the server's stale copy and deleted cards come back.
  useEffect(() => { try { localStorage.setItem("lsg_bps_urls", urls); scheduleFlush(); } catch { /* ignore */ } }, [urls]);
  useEffect(() => { try { localStorage.setItem("lsg_bps_cards", cardsHtml); scheduleFlush(); } catch { /* ignore */ } }, [cardsHtml]);

  // Track mount state so the crawl only calls setState when this instance is still on screen.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // If a crawl started before we switched away is still running (or just finished), re-attach on
  // mount: show it as building and, when it resolves, pick up the cards it wrote to localStorage.
  useEffect(() => {
    if (bpsInFlight) {
      // Still running (we only switched app tabs) → re-attach and reflect progress + result.
      setBuilding(true);
      if (bpsStatus) setStatus(bpsStatus);
      void bpsInFlight.then(() => {
        if (!mounted.current) return;
        setCardsHtml(localStorage.getItem("lsg_bps_cards") ?? "");
        setStatus(bpsStatus);
        setBuilding(false);
      });
      return;
    }
    // Not running: was a crawl interrupted (browser reload/close mid-crawl)? A leftover pending
    // marker with no in-flight promise means yes — offer to Resume it.
    try {
      const pend = JSON.parse(localStorage.getItem("lsg_bps_pending") ?? "[]") as unknown;
      const list = Array.isArray(pend) ? pend.filter((u): u is string => typeof u === "string") : [];
      if (list.length) {
        setInterrupted(list);
        setStatus(`A previous schema crawl was interrupted — ${list.length} page${list.length === 1 ? "" : "s"} didn't finish. Click Resume to retry.`);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove one card (trash button) from the stored cards HTML.
  const removeCard = useCallback((cardId: string) => {
    setCardsHtml(prev => {
      const tmp = document.createElement("div");
      tmp.innerHTML = prev;
      tmp.querySelector(`[id="${cardId}"]`)?.remove();
      return tmp.innerHTML.trim();
    });
  }, []);
  // Inject + wire a per-card trash button on each card.
  useEffect(() => { const r = cardsRef.current; if (!r) return; return wireCardTrash(r, removeCard); }, [cardsHtml, removeCard]);

  // The crawl itself (shared by Continue and Resume). Runs as a module-tracked promise that writes
  // its result STRAIGHT to localStorage — so switching tabs (unmount) can't drop the cards — and
  // marks the URLs "pending" so a browser reload/close mid-crawl can offer to Resume them.
  const runCrawl = useCallback(async (fresh: string[]) => {
    const anthropicKey = loadAnthropicKey();
    if (!anthropicKey) {
      void showAlert("Anthropic API key not set.\n\nGo to ⚙ Settings and add your Anthropic API key (sk-ant-…) to generate schema.", { title: "API Key Required" });
      return;
    }
    setInterrupted([]);
    setBuilding(true);
    // Process everything in one Continue click, in batches (the server caps each request at 25).
    // Each batch's cards are persisted before the next starts, so a slow/failed batch never loses
    // the ones already done, and a mid-crawl reload can Resume only the URLs still pending.
    const BATCH = 25;
    const total = fresh.length;
    bpsStatus = total > BATCH
      ? `Crawling ${total} pages in batches of ${BATCH} and generating schema… (0 of ${total} done)`
      : `Crawling ${total} new page${total === 1 ? "" : "s"} and generating schema… (this can take 15–60 sec)`;
    setStatus(bpsStatus);
    // Record the in-progress URLs so a reload/close mid-crawl leaves a trail we can Resume from.
    try { localStorage.setItem("lsg_bps_pending", JSON.stringify(fresh)); } catch { /* ignore */ }
    // Authoritative industry signal for schema type selection: prefer the verified GBP primary
    // category (from the crawl), fall back to the manually-entered cover category.
    const industry = (() => {
      try {
        const fm = JSON.parse(localStorage.getItem("seo_full_metrics") ?? "null") as { schemaSource?: { gbp?: { category?: string; description?: string } } } | null;
        return {
          category: ((fm?.schemaSource?.gbp?.category ?? "").trim() || (localStorage.getItem("cover_gbp_category") ?? "").trim()) || undefined,
          description: (fm?.schemaSource?.gbp?.description ?? "").trim() || undefined,
        };
      } catch { return { category: (localStorage.getItem("cover_gbp_category") ?? "").trim() || undefined, description: undefined }; }
    })();
    const run = (async () => {
      let done = 0, added = 0, failed = "";
      try {
        for (let s = 0; s < fresh.length; s += BATCH) {
          const batch = fresh.slice(s, s + BATCH);
          if (total > BATCH) {
            bpsStatus = `Crawling pages ${done + 1}–${done + batch.length} of ${total}… generating schema (${done} of ${total} done).`;
            if (mounted.current) setStatus(bpsStatus);
          }
          // Recompute the starting card index each batch — the stored cards grew as batches landed,
          // so IDs never collide across batches.
          const startIndex = nextCardIndex(localStorage.getItem("lsg_bps_cards") ?? "");
          const res = await fetch("/api/bulk-page-schema", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              urls: batch,
              startIndex,
              anthropicKey,
              brand: (localStorage.getItem("cover_business_name") ?? "").trim() || undefined,
              location: (localStorage.getItem("cover_location") ?? "").trim() || undefined,
              gbpCategory: industry.category,
              gbpDescription: industry.description,
            }),
          });
          const data = await res.json() as { ok: boolean; html?: string; error?: string };
          if (data.ok && data.html) {
            const prev = localStorage.getItem("lsg_bps_cards") ?? "";
            try { localStorage.setItem("lsg_bps_cards", prev ? `${prev}\n${data.html}` : data.html); } catch { /* ignore */ }
            added += batch.length;
            if (mounted.current) setCardsHtml(localStorage.getItem("lsg_bps_cards") ?? ""); // let cards appear as each batch lands
          } else {
            failed = data.error ?? "Failed to generate schema";
            break; // stop the loop; cards from prior batches are already saved
          }
          done += batch.length;
          // Shrink the resume trail to just the URLs still pending.
          try { const rest = fresh.slice(done); if (rest.length) localStorage.setItem("lsg_bps_pending", JSON.stringify(rest)); else localStorage.removeItem("lsg_bps_pending"); } catch { /* ignore */ }
        }
        bpsStatus = failed
          ? `⚠ ${failed}${added ? ` — ${added} card${added === 1 ? "" : "s"} added before the error; click Continue to retry the rest.` : ""}`
          : `Added ${added} new schema card${added === 1 ? "" : "s"} — review each and click ⚡ Approve to inject it into that page.`;
      } catch (e) {
        bpsStatus = `⚠ ${e instanceof Error ? e.message : "Network error"}${added ? ` — ${added} card${added === 1 ? "" : "s"} added before the error; click Continue to retry the rest.` : ""}`;
      }
      // Completed (success or a handled error) → it wasn't reload-interrupted → clear the marker.
      try { localStorage.removeItem("lsg_bps_pending"); } catch { /* ignore */ }
    })();
    bpsInFlight = run;
    void run.finally(() => { if (bpsInFlight === run) bpsInFlight = null; });
    await run;
    // Reflect the result only if THIS instance is still mounted (a returning instance re-attaches
    // via the mount effect below and reads the same localStorage the crawl just wrote).
    if (mounted.current) {
      setCardsHtml(localStorage.getItem("lsg_bps_cards") ?? "");
      setStatus(bpsStatus);
      setBuilding(false);
    }
  }, []);

  async function handleContinue() {
    const list = urls.split("\n").map(u => u.trim()).filter(Boolean);
    const valid = list.filter(u => /^https?:\/\//i.test(u));
    if (!valid.length) {
      void showAlert("Paste at least one full page URL (starting with https://), one per line.", { title: "No URLs" });
      return;
    }
    // Only crawl URLs that don't already have a card; also drop duplicates within the box.
    const carded = extractCardedUrls(cardsHtml);
    const seen = new Set<string>();
    const fresh = valid.filter(u => {
      const key = normalizeUrl(u);
      if (carded.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!fresh.length) {
      void showAlert("Every URL in the box already has a schema card. Add one or more new URLs to crawl.", { title: "Nothing new to crawl" });
      return;
    }
    const subject = fresh.length === 1 ? "This 1 new page" : `These ${fresh.length} new pages`;
    const proceed = await showConfirm(
      `${subject} will be crawled live to determine the most appropriate, JSON-LD schema for each (based on the page type). A schema card will be created for each page.`,
      { title: "Crawl pages for schema", confirmLabel: "OK", cancelLabel: "Cancel", tone: "info" },
    );
    if (!proceed) return;
    await runCrawl(fresh);
  }

  // Resume a crawl that was interrupted by a reload/close — re-crawl only the URLs that never got a card.
  const resumeCrawl = () => {
    const carded = extractCardedUrls(localStorage.getItem("lsg_bps_cards") ?? "");
    const seen = new Set<string>();
    const fresh = interrupted.filter(u => { const k = normalizeUrl(u); if (carded.has(k) || seen.has(k)) return false; seen.add(k); return true; });
    if (!fresh.length) { setInterrupted([]); try { localStorage.removeItem("lsg_bps_pending"); } catch { /* ignore */ } return; }
    void runCrawl(fresh);
  };

  return (
    <div className="content-drawer" style={{ marginTop: 24 }}>
      <div className="cd-section">
        <div className="cd-section-hd">
          <span className="cd-hd-brand">Bulk Page Schema</span>
          <span className="audit-tip" style={{ marginLeft: 2 }}>
            <span className="audit-tip-icon">i</span>
            <span className="audit-tip-box" style={{ textTransform: "none", fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, letterSpacing: "normal" }}>
              {"Paste any page URLs from your site and the tool crawls each one live to determine the page type and business type, then generates the most robust, complete JSON-LD schema for it. Each page gets its own card — click Approve to inject the schema into that page's <head>. No site crawl required."}
            </span>
          </span>
        </div>
        <p className="cd-help">
          Paste one page URL per line, then click <strong>Continue</strong>. Each page is crawled and gets a schema
          card — click <strong>⚡ Approve</strong> on a card to inject its JSON-LD. Works without running a site crawl.
        </p>
        <textarea
          className="cd-titles"
          value={urls}
          onChange={e => setUrls(e.target.value)}
          rows={5}
          placeholder={"https://yoursite.com/services/\nhttps://yoursite.com/about/\nhttps://yoursite.com/contact/"}
        />
        <div className="cd-actions">
          <button className="cd-continue-btn" onClick={handleContinue} disabled={building}>
            {building ? "Working…" : "Continue →"}
          </button>
          {interrupted.length > 0 && !building && (
            <button className="cd-continue-btn" style={{ background: "#d97706" }} onClick={resumeCrawl}>
              ⏯ Resume ({interrupted.length})
            </button>
          )}
          {status && <span className="cd-status">{status}</span>}
        </div>
      </div>

      {/* Generated schema cards — rendered through AuditSection (id="schema") so they get
          the Approve wiring (→ execSchemaFix) and the auto-injected "Check schema in Google" link. */}
      {cardsHtml && (
        <div className="cd-cards" ref={cardsRef}>
          <AuditSection
            id="schema"
            html={cardsHtml}
            state={props.state}
            siteBrand={props.siteBrand}
            auditDate={props.auditDate}
            onApprove={props.onApprove}
            onIgnore={props.onIgnore}
            onDone={props.onDone}
            onReset={props.onReset}
            hidden={props.hidden}
            onToggleHide={props.onToggleHide}
            connected={props.connected}
            fetchPageTags={props.fetchPageTags}
            onOpenSchemaSetup={props.onOpenSchemaSetup}
            onViewSchema={props.onViewSchema}
            onViewIssues={props.onViewIssues}
            manualSchema={props.manualSchema}
            onManualUpdate={props.onManualUpdate}
            onPredictManual={props.onPredictManual}
            getClient={props.getClient}
          />
        </div>
      )}

      {/* History of pages this tool has added schema to (via the durable _audit_schema meta). */}
      <SchemaHistory connected={props.connected} getClient={props.getClient} />
    </div>
  );
}
